import { callClaudeWorker } from "./claudeWorker";
import { ROOM_FUNCTION_FRAMEWORK } from "./roomFunctionFramework";
import type { EquipmentCounts } from "./equipmentTypes";
import Logger from "../../Logger";

const logger = new Logger();

// Beschreibt jeden unserer internen Ausstattungs-Schlüssel für Claude, inkl. der
// Zuordnung der beiden Steckdosen-Varianten aus dem Rahmenwerk: "Steckdose" (ohne Zusatz)
// im Rahmenwerk ist eine normale, ungeschaltete Steckdose (steckdose_dauerstrom); "Steckdose
// geschaltet" ist eine KNX-schaltbare Steckdose (steckdose).
const EQUIPMENT_KIND_GLOSSARY = `- licht_schalt: geschaltete Leuchte (nur ein/aus)
- licht_dimm: gedimmte Leuchte
- licht_tw: Leuchte mit einstellbarer Farbtemperatur (Tunable White)
- licht_rgb: Leuchte mit RGB-Farbsteuerung
- jalousie: Jalousie (Lamellen-Beschattung)
- rolladen: Rollladen (undurchsichtige Beschattung). Nutze "jalousie", wenn das Rahmenwerk
  nur "Rollladen/Jalousie" ohne weitere Angabe nennt.
- heizung: Heizungsaktor für den Raum
- steckdose: KNX-geschaltete Steckdose (entspricht Rahmenwerk-Eintrag "Steckdose geschaltet")
- steckdose_dauerstrom: normale, ungeschaltete Steckdose mit Dauerstrom (entspricht
  Rahmenwerk-Eintrag "Steckdose" ohne Zusatz)
- sensor: Präsenz-/Bewegungsmelder (Rahmenwerk-Eintrag "Sensor")
- taster: Taster/Schalter für manuelle Bedienung
- kontakt: Fenster-/Türkontakt (Rahmenwerk-Eintrag "Kontakt") - NICHT mit "sensor" verwechseln,
  das ist ein eigenständiges Element für Bewegungsmelder

Ignoriere Rahmenwerk-Einträge, die keinem dieser Schlüssel entsprechen (z.B. "Verbraucher 400V",
"230V Verbraucher") – dafür gibt es aktuell keine Ausstattungs-Kachel.`;

const SUGGESTION_PROMPT_INTRO = `Du bist ein Assistent für Elektro-/KNX-Planung. Im Folgenden bekommst du ein Rahmenwerk
(Referenz-Tabelle Raumtyp -> Standard-Funktionen), das die übliche Ausstattung je Raumtyp
beschreibt, sowie eine Liste tatsächlicher Räume (Bezeichnung und, falls bekannt, Fläche in m²).

Ordne jeden Raum dem am besten passenden Raumtyp aus dem Rahmenwerk zu (bei Bezeichnungen wie
"Bad", "Badezimmer", "WC" etc. anhand der Fläche zwischen den ggf. vorhandenen Größen-Varianten
unterscheiden; ist keine Fläche bekannt, die häufigere/mittlere Variante annehmen) und leite
daraus die vorzuschlagende Ausstattung ab. Übersetze die Rahmenwerk-Funktionen anhand des
folgenden Glossars in unsere internen Schlüssel:

${EQUIPMENT_KIND_GLOSSARY}

Gib für jeden Raum die ANZAHL je Schlüssel an (z.B. zwei "Steckdose"-Zeilen im Rahmenwerk für
denselben Raumtyp -> "steckdose_dauerstrom": 2).`;

function buildPrompt(rooms: { roomId: string; name: string; flaecheM2: number | null }[]): string {
  const roomList = rooms.map((r) => ({ roomId: r.roomId, name: r.name, flaecheM2: r.flaecheM2 }));
  return `${SUGGESTION_PROMPT_INTRO}

Rahmenwerk:
${ROOM_FUNCTION_FRAMEWORK}

Räume:
${JSON.stringify(roomList)}

Antworte AUSSCHLIESSLICH mit validem JSON in genau dieser Form, ohne Markdown, ohne Codeblöcke:
{ "raeume": [ { "roomId": string, "ausstattung": { "<schlüssel>": number, ... } } ] }`;
}

function parseSuggestionResponse(text: string): Map<string, EquipmentCounts> {
  const result = new Map<string, EquipmentCounts>();
  const clean = text.replace(/```json|```/g, "").trim();
  if (!clean) {
    return result;
  }
  const parsed = JSON.parse(clean);
  const raeume = parsed.raeume || [];
  raeume.forEach((entry: any) => {
    if (entry && typeof entry.roomId === "string" && entry.ausstattung) {
      result.set(entry.roomId, entry.ausstattung);
    }
  });
  return result;
}

// Schlägt für die übergebenen Räume (Bezeichnung + ggf. Fläche) anhand des Rahmenwerks
// (roomFunctionFramework.ts) die Standard-Ausstattung vor - ein gebündelter Aufruf für
// alle Räume statt einem Aufruf je Raum. Wirft nie: bei Fehlern (Netzwerk, Parse-Fehler,
// leere Antwort) wird eine leere Map zurückgegeben, damit der Assistent nicht blockiert -
// betroffene Räume fallen dann auf die lokale Heuristik zurück (siehe equipmentAssumptions.ts).
export async function suggestEquipmentForRooms(
  rooms: { roomId: string; name: string; flaecheM2: number | null }[],
): Promise<Map<string, EquipmentCounts>> {
  if (rooms.length === 0) {
    return new Map();
  }
  try {
    const text = await callClaudeWorker(buildPrompt(rooms), 8192);
    return parseSuggestionResponse(text);
  } catch (err) {
    logger.error(err);
    return new Map();
  }
}
